import * as Phaser from "phaser"
import { gameConfig } from "./config"
import { EventBus } from "./core/event-bus"
import { unlockAudioContext } from "./systems/sfx-generator"

type AppWindow = Window & {
  __phaserGame?: Phaser.Game
  __bootPhaser?: () => Phaser.Game
  __unlockAudio?: () => void
}

let game: Phaser.Game | null = null

function bootGame(): Phaser.Game {
  if (game) {
    return game
  }

  game = new Phaser.Game(gameConfig)
  // game.sound.pauseOnBlur = false
  EventBus.init(game)

  // Orientation handling for mobile
  const checkOrientation = (orientation: string | number) => {
    const overlay = document.getElementById("orientation-overlay")
    if (
      orientation === Phaser.Scale.Orientation.PORTRAIT ||
      orientation === "portrait-primary" ||
      orientation === "portrait-secondary"
    ) {
      if (!overlay) {
        const div = document.createElement("div")
        div.id = "orientation-overlay"
        div.innerHTML = `
          <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;color:white;font-family:sans-serif;text-align:center;padding:20px;background:rgba(0,0,0,0.9);position:fixed;inset:0;z-index:100000;">
            <div style="font-size:40px;margin-bottom:20px;">🔄</div>
            <div style="font-weight:bold;margin-bottom:10px;">PLEASE ROTATE YOUR DEVICE</div>
            <div style="font-size:14px;opacity:0.8;">This tactical command interface requires landscape orientation.</div>
          </div>
        `
        document.body.appendChild(div)
      }
    } else {
      overlay?.remove()
      // Dispatch event for auto-fullscreen in index.html
      window.dispatchEvent(new CustomEvent("game-orientation-landscape"))
    }
  }

  game.scale.on("orientationchange", checkOrientation)

  // Initial check
  game.events.on("ready", () => {
    if (game) checkOrientation(game.scale.orientation)
    const canvas = game?.canvas
    if (canvas) {
      canvas.style.imageRendering = "auto"
    }
  })
  ;(window as Window & { __phaserGame?: Phaser.Game }).__phaserGame = game

  return game
}

// Expose deferred boot + audio unlock in index.html
const appWindow = window as AppWindow
appWindow.__bootPhaser = bootGame
appWindow.__unlockAudio = () => unlockAudioContext()

// Listen for Workbench Playground commands
window.addEventListener("message", (event) => {
  if (event.data && typeof event.data.type === "string") {
    switch (event.data.type) {
      case "start-game":
        if (appWindow.__bootPhaser) appWindow.__bootPhaser()
        break
      case "warp-to-wave":
        EventBus.emit("debug-warp-to-wave", event.data.payload?.wave)
        break
      case "toggle-debug-physics":
        if (game) {
          const arcadeConfig = game.config.physics.arcade
          if (!arcadeConfig) break
          arcadeConfig.debug = event.data.payload?.enabled
          // We'd need to restart the scene for physics debug changes to apply easily,
          // or manually toggle it. Emitting an event so the active scene can handle it if needed.
          EventBus.emit("debug-toggle-physics", event.data.payload?.enabled)
        }
        break
      case "kill-all-enemies":
        EventBus.emit("debug-kill-all-enemies")
        break
      case "give-money":
        EventBus.emit("debug-give-money", event.data.payload?.amount)
        break
      case "set-hp":
        EventBus.emit("debug-set-hp", event.data.payload?.amount)
        break
      case "set-all-stats-level":
        EventBus.emit("debug-set-all-stats-level", event.data.payload?.level)
        break
      case "reload-scene":
        EventBus.emit("debug-reload-scene")
        break
      case "spawn-enemy":
        EventBus.emit("debug-spawn-enemy", event.data.payload)
        break
      case "toggle-invincible":
        EventBus.emit("debug-toggle-invincible", event.data.payload?.enabled)
        break
    }
  }
})
